import { useUserProfile } from '@/contexts/UserProfileContext';

type AppRole = 'owner' | 'admin' | 'supervisor' | 'production_worker';

const ROLE_LEVEL: Record<AppRole, number> = {
  owner: 4,
  admin: 3,
  supervisor: 2,
  production_worker: 1,
};

export function usePermissions() {
  const { userProfile } = useUserProfile();
  const role = (userProfile?.role ?? 'production_worker') as AppRole;
  const level = ROLE_LEVEL[role] ?? 1;

  return {
    role,

    // Financial visibility
    canViewFinancials: level >= ROLE_LEVEL.admin,

    // Staff
    canAccessStaff: level >= ROLE_LEVEL.supervisor,
    canManageStaff: level >= ROLE_LEVEL.admin,

    // Billing - owner only
    canAccessBilling: role === 'owner',

    // Settings
    canAccessSettings: level >= ROLE_LEVEL.admin,

    // Catalog editing (ingredients, materials, recipes, variants, packages, products)
    canEditCatalog: level >= ROLE_LEVEL.admin,

    // Contacts (customers, suppliers)
    canAccessContacts: level >= ROLE_LEVEL.supervisor,
    canEditContacts: level >= ROLE_LEVEL.admin,

    // Orders (MO, SO, TO, PO)
    canEditOrders: level >= ROLE_LEVEL.supervisor,
    canCreateOrders: level >= ROLE_LEVEL.supervisor,
    canAccessPurchaseOrders: level >= ROLE_LEVEL.supervisor,
    canAccessSalesOrders: level >= ROLE_LEVEL.supervisor,
    /*
     * S274-14: Owner + Admin only, a rung ABOVE its siblings above. A weekly log carries every
     * worker's hours and pay rate, which is exactly the payroll data `canViewFinancials` exists to
     * keep from supervisors — and completing one is an undeletable write that creates make orders
     * and moves stock.
     */
    canAccessProductionLogs: level >= ROLE_LEVEL.admin,
    canAccessFulfillment: true,

    // Resource editing (legacy, kept for backwards compat)
    canEditResources: level >= ROLE_LEVEL.supervisor,

    // Role checks
    isAtLeastSupervisor: level >= ROLE_LEVEL.supervisor,
  };
}
